import React, { useState } from 'react';
import { supabase } from '../supabase';
import { API_BASE_URL } from '../config';

function AuthModal({ isOpen, onClose, onAuthSuccess }) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = async (email) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/validate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Email validation error:', error);
      return { isValid: false, error: 'Failed to validate email' };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsLoading(true);
    setError('');

    // Validate email first
    const validation = await validateEmail(email.trim());
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    try {
      console.log('Attempting to insert email:', email.trim());
      
      // First, try to check if user already exists
      const { data: existingUser, error: checkError } = await supabase
        .from('whatifai')
        .select('user, credits')
        .eq('user', email.trim())
        .single();

      console.log('Check existing user:', { existingUser, checkError });

      if (existingUser) {
        // User exists, proceed with login
        console.log('User already exists, proceeding with login');
        localStorage.setItem('whatifai_user', email.trim());
        onAuthSuccess(email.trim(), existingUser.credits || 0);
        onClose();
        return;
      }

      // User doesn't exist, insert new user with default credits
      const { data, error } = await supabase
        .from('whatifai')
        .insert([{ user: email.trim(), credits: 100 }]) // Give new users 100 credits
        .select();

      console.log('Insert new user response:', { data, error });

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }

      // Store email in localStorage for session
      localStorage.setItem('whatifai_user', email.trim());
      onAuthSuccess(email.trim(), 100); // New users get 100 credits
      onClose();
      
    } catch (error) {
      console.error('Auth error:', error);
      setError(`Failed to sign in: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-mono font-bold mb-2">
            Sign in to What if AI
          </h2>
          <p className="text-muted font-mono">
            Enter your email to continue
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="terminal-input w-full"
              required
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="text-red-600 font-mono text-sm text-center">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 plain-link text-center py-2 border border-gray-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!email.trim() || isLoading}
              className="flex-1 bg-black text-white font-mono py-2 disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AuthModal; 